import { useState, useRef, useEffect } from "react";
import { Volume2, VolumeX } from "lucide-react";

const MUSIC_URL = "https://customer-assets.emergentagent.com/job_f762e596-06ed-44e3-86cd-d6690b2cb9fa/artifacts/q180prz4_HUGEL%2C%20Ultra%20Nate%2C%20Imael%20Angel%20-%20Movin%E2%80%99%20To%20The%20Sun%20%20%5BMake%20The%20Girls%20Dance%20Records%5D.m4a";

const MusicPlayer = ({ hasActiveEvents = false }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [userWantsMusic, setUserWantsMusic] = useState(true);
  const [hasInteracted, setHasInteracted] = useState(false);
  const audioRef = useRef(null);

  // Detect first user interaction and start music
  useEffect(() => {
    if (!hasActiveEvents || hasInteracted) return;

    const startMusicOnInteraction = () => {
      if (!hasInteracted && audioRef.current && userWantsMusic) {
        setHasInteracted(true);
        audioRef.current.play()
          .then(() => setIsPlaying(true))
          .catch(() => {});
      }
    };

    // Listen for any user interaction
    const events = ['click', 'touchstart', 'scroll', 'keydown'];
    events.forEach(event => {
      document.addEventListener(event, startMusicOnInteraction, { once: true, passive: true });
    });

    return () => {
      events.forEach(event => {
        document.removeEventListener(event, startMusicOnInteraction);
      });
    };
  }, [hasActiveEvents, hasInteracted, userWantsMusic]);

  // Handle page visibility changes
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!audioRef.current) return;
      
      if (document.hidden) {
        // Page is hidden - pause music
        if (isPlaying) {
          audioRef.current.pause();
          setIsPlaying(false);
        }
      } else {
        // Page is visible - resume if user wants music and has interacted
        if (userWantsMusic && hasActiveEvents && hasInteracted) {
          audioRef.current.play()
            .then(() => setIsPlaying(true))
            .catch(() => {});
        }
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, [isPlaying, userWantsMusic, hasActiveEvents, hasInteracted]);

  // Stop music if no active events
  useEffect(() => {
    if (!hasActiveEvents && isPlaying && audioRef.current) {
      audioRef.current.pause();
      setIsPlaying(false);
    }
  }, [hasActiveEvents, isPlaying]);

  const togglePlay = () => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
        setIsPlaying(false);
        setUserWantsMusic(false);
      } else {
        audioRef.current.play()
          .then(() => {
            setIsPlaying(true);
            setUserWantsMusic(true);
            setHasInteracted(true);
          })
          .catch(e => console.log("Play blocked:", e));
      }
    }
  };

  // Don't render if no active events
  if (!hasActiveEvents) return null;

  return (
    <>
      <audio 
        ref={audioRef} 
        src={MUSIC_URL} 
        loop 
        preload="auto"
      />
      
      {/* Floating Music Button */}
      <div className="fixed bottom-6 right-6 z-50 flex items-center gap-3">
        {/* Button */}
        <button
          onClick={togglePlay}
          className={`group relative w-14 h-14 rounded-full flex items-center justify-center transition-all duration-300 shadow-lg ${
            isPlaying 
              ? 'bg-[var(--primary)] hover:bg-[var(--primary)]/80 shadow-[0_0_20px_rgba(236,0,232,0.5)]' 
              : 'bg-[var(--surface)] border-2 border-[var(--primary)]/50 hover:border-[var(--primary)] hover:shadow-[0_0_15px_rgba(236,0,232,0.3)]'
          }`}
          title={isPlaying ? "Pausar música" : "Reproducir música"}
          data-testid="music-toggle-btn"
        >
          {/* Animated rings when playing */}
          {isPlaying && (
            <>
              <span className="absolute inset-0 rounded-full border-2 border-[var(--primary)] animate-ping opacity-30" />
              <span className="absolute inset-[-4px] rounded-full border border-[var(--primary)]/30 animate-pulse" />
            </>
          )}
          
          {isPlaying ? (
            <Volume2 size={24} className="text-white relative z-10" />
          ) : (
            <VolumeX size={24} className="text-[var(--primary)] group-hover:scale-110 transition-transform relative z-10" />
          )}
        </button>
      </div>
    </>
  );
};

export default MusicPlayer;
